import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import { Search, RotateCcw, ChevronLeft, ChevronRight, ScrollText, MessageCircle, User, X } from 'lucide-react';
import dayjs from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { cn, DatePicker } from '../../ui';
import { formatIst } from '../../lib/time';
import { TABS, MODULE_BADGE, actionBadge, ACTION_CHIPS_BY_TAB } from './activityConstants';
import { ActivityLogDetailDrawer, type ActivityRow } from './ActivityLogDetailDrawer';
import { SmsLogDetailDrawer, SMS_STATUS_BADGE, type SmsLogRow } from './SmsLogDetailDrawer';

type Mode = 'logs' | 'whatsapp';
interface ActivityResp { activity_logs: ActivityRow[]; pagination: Pagination }
interface SmsResp { sms_logs: SmsLogRow[]; pagination: Pagination }
interface Pagination { page: number; limit: number; total: number; pages: number }
interface SmsStats { total: number; today: number; SENT: number; FAILED: number; SKIPPED: number; STUB: number }

const PAGE_SIZE = 25;
const SMS_STATUSES = ['', 'SENT', 'FAILED', 'SKIPPED', 'STUB'];

function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30); if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}
const todayStr = () => new Date().toLocaleDateString('en-CA');

const FIELD_CLS = 'rounded-[10px] border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

/**
 * Actor filter: type-to-search over STAFF names (never renders the whole user list — only the matches for what
 * you type), and filters the log by the chosen user's id. `value` is the selected actor id ('' = no filter);
 * clearing it (e.g. the Reset button) resets the picker. Backed by GET /users?search=…&limit=20 (USER.VIEW).
 */
function ActorFilter({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const [text, setText] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState(''); // label of the currently-applied actor ('' = none)
  const [active, setActive] = useState(0); // keyboard-highlighted option index
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(text.trim()), 250); return () => clearTimeout(t); }, [text]);
  // External reset (parent clears the actor id — Reset button, or leaving 'logs' mode) → clear the picker.
  useEffect(() => { if (!value) { setSelectedName(''); setText(''); setDebounced(''); setOpen(false); } }, [value]);
  // Outside click: close the dropdown and revert any unpicked typing back to the applied selection.
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setOpen(false); setText(selectedName); }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [selectedName]);

  const q = useQuery({
    queryKey: ['actor-search', debounced],
    enabled: open && debounced.length >= 1,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // Dedicated actor search: matches NAME or ROLE (not email), staff only, ACTIVITY.VIEW-gated.
    queryFn: () => request<{ data: any[] }>({ url: '/activity-logs/actors', params: { search: debounced } }),
  });
  const users = q.data?.data ?? [];
  useEffect(() => { setActive(0); }, [debounced]); // reset the highlight when the search term changes

  const pick = (u: any) => {
    if (!u) return;
    const name = u.name || `#${u.id}`;
    setSelectedName(name); setText(name); setOpen(false);
    onChange(String(u.id)); // the applied filter changes ONLY on an explicit pick (never on a keystroke)
  };
  const clear = () => { setSelectedName(''); setText(''); setDebounced(''); setOpen(false); onChange(''); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); setText(selectedName); return; }
    if (!open) { if (e.key === 'ArrowDown') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, users.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(users[active]); }
  };

  const listId = 'actor-listbox';
  const showList = open && debounced.length >= 1;
  return (
    <div ref={boxRef} className="relative w-52">
      <User className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted" />
      <input
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Actor (name / role)…"
        aria-label="Filter by actor name or role"
        role="combobox" aria-expanded={showList} aria-controls={listId} aria-autocomplete="list"
        className={cn(FIELD_CLS, 'w-full pl-9', text ? 'pr-8' : '')}
      />
      {text && (
        <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-content" aria-label="Clear actor">
          <X className="size-3.5" />
        </button>
      )}
      {showList && (
        // Dim + disable clicks while a newer search is in flight so a stale row can't be mis-picked.
        <div id={listId} role="listbox" className={cn('absolute z-20 mt-1 max-h-64 w-72 overflow-auto rounded-[10px] border border-line bg-surface py-1 shadow-lg', q.isFetching && 'pointer-events-none opacity-60')}>
          {q.isFetching && !users.length ? (
            <div className="px-3 py-2 text-xs text-muted">Searching…</div>
          ) : users.length ? users.map((u: any, i: number) => (
            <button key={u.id} type="button" role="option" aria-selected={i === active} onMouseEnter={() => setActive(i)} onClick={() => pick(u)}
              className={cn('flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm', i === active ? 'bg-surface-2' : 'hover:bg-surface-2')}>
              <span className="truncate text-content">{u.name || `#${u.id}`}</span>
              {(u.roleName ?? u.roleCode) && (
                <span className="shrink-0 whitespace-nowrap rounded-full border border-line bg-surface-3 px-2 py-0.5 text-2xs font-medium uppercase tracking-wide text-muted">{u.roleName ?? u.roleCode}</span>
              )}
            </button>
          )) : (
            <div className="px-3 py-2 text-xs text-muted">No matching staff.</div>
          )}
        </div>
      )}
    </div>
  );
}

export function ActivityLogsPage() {
  const [mode, setMode] = useState<Mode>('logs');

  // shared
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  // activity-mode filters
  const [tabId, setTabId] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actorId, setActorId] = useState('');
  const [selected, setSelected] = useState<ActivityRow | null>(null);

  // whatsapp-mode filters
  const [smsStatus, setSmsStatus] = useState('');
  const [smsTemplate, setSmsTemplate] = useState('');
  const [smsMobile, setSmsMobile] = useState('');
  const [smsSelected, setSmsSelected] = useState<SmsLogRow | null>(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [mode, tabId, debounced, actionFilter, actorId, fromDate, toDate, smsStatus, smsTemplate, smsMobile]);
  // The actor filter is logs-only; drop it when leaving 'logs' so switching modes can't leave the log silently
  // filtered while the (unmounted) picker shows a blank input.
  useEffect(() => { if (mode !== 'logs') setActorId(''); }, [mode]);

  const dateParams = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (fromDate) p.from = `${fromDate} 00:00:00`;
    if (toDate) p.to = `${toDate} 23:59:59`;
    return p;
  }, [fromDate, toDate]);

  // ---------------- Activity mode ----------------
  const activeTab = useMemo(() => TABS.find((t) => t.id === tabId) || TABS[0], [tabId]);
  const actFilters = useMemo(() => ({
    ...dateParams,
    ...(debounced ? { search: debounced } : {}),
    ...(actionFilter ? { action: actionFilter } : {}),
    ...(actorId ? { actor_id: actorId } : {}),
  }), [dateParams, debounced, actionFilter, actorId]);

  const actListQuery = useQuery({
    queryKey: ['activity-logs', actFilters, activeTab.id, page],
    enabled: mode === 'logs',
    placeholderData: keepPreviousData,
    queryFn: () => request<ActivityResp>({ url: '/activity-logs', params: { ...actFilters, page, limit: PAGE_SIZE, sort: 'desc', ...(activeTab.modules.length ? { modules: activeTab.modules.join(',') } : {}) } }),
  });
  const tabCountQueries = useQueries({
    queries: TABS.map((t) => ({
      queryKey: ['activity-logs', 'tab-count', t.id, actFilters],
      enabled: mode === 'logs',
      staleTime: 30_000,
      placeholderData: keepPreviousData,
      queryFn: () => request<ActivityResp>({ url: '/activity-logs', params: { ...actFilters, page: 1, limit: 1, sort: 'desc', ...(t.modules.length ? { modules: t.modules.join(',') } : {}) } }),
    })),
  });
  const tabCounts = useMemo(() => { const m: Record<string, number | null> = {}; TABS.forEach((t, i) => { m[t.id] = tabCountQueries[i]?.data?.pagination?.total ?? null; }); return m; }, [tabCountQueries]);

  const today = todayStr();
  const span = { from: `${today} 00:00:00`, to: `${today} 23:59:59` };
  const actStatQueries = useQueries({
    queries: [
      { key: 'total', modules: '' }, { key: 'sales', modules: 'ORDER,ORDER_APPROVAL,SHIPMENT,INVOICE,PAYMENT' },
      { key: 'field', modules: 'COMPLAINT,COMPLAINT_THREAD,EVENT,VISIT,FFA' }, { key: 'people', modules: 'USER,RETAILER,FARMER' },
    ].map((s) => ({
      queryKey: ['activity-logs', 'stat', s.key, today],
      enabled: mode === 'logs', staleTime: 60_000,
      queryFn: () => request<ActivityResp>({ url: '/activity-logs', params: { ...span, page: 1, limit: 1, sort: 'desc', ...(s.modules ? { modules: s.modules } : {}) } }),
    })),
  });

  // ---------------- WhatsApp mode ----------------
  const smsListQuery = useQuery({
    queryKey: ['sms-logs', dateParams, smsStatus, smsTemplate, smsMobile, page],
    enabled: mode === 'whatsapp',
    placeholderData: keepPreviousData,
    queryFn: () => request<SmsResp>({ url: '/sms-logs', params: { ...dateParams, page, limit: PAGE_SIZE, ...(smsStatus ? { status: smsStatus } : {}), ...(smsTemplate ? { templateName: smsTemplate } : {}), ...(smsMobile ? { mobile: smsMobile } : {}) } }),
  });
  const smsStatsQuery = useQuery({ queryKey: ['sms-logs', 'stats'], enabled: mode === 'whatsapp', staleTime: 60_000, queryFn: () => request<SmsStats>({ url: '/sms-logs/stats' }) });

  // ---------------- shared derived ----------------
  const pg: Pagination = (mode === 'logs' ? actListQuery.data?.pagination : smsListQuery.data?.pagination) ?? { page: 1, pages: 1, total: 0, limit: PAGE_SIZE };
  const isFetching = mode === 'logs' ? actListQuery.isFetching : smsListQuery.isFetching;
  const isLoading = mode === 'logs' ? actListQuery.isLoading : smsListQuery.isLoading;

  function reset() {
    setSearch(''); setDebounced(''); setActionFilter(''); setActorId(''); setFromDate(''); setToDate(''); setTabId('all');
    setSmsStatus(''); setSmsTemplate(''); setSmsMobile('');
  }

  const inputCls = 'rounded-[10px] border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-500';
  // Filter chips (action + WhatsApp status): soft-green when active — matches the module tabs above, instead of
  // the jarring solid-green fill that read as out-of-place.
  const chipBase = 'rounded-full border px-2.5 py-0.5 text-2xs font-medium transition-colors';
  const chipActive = 'border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-500/60 dark:bg-brand-900/25 dark:text-brand-200';
  const chipIdle = 'border-line text-content hover:bg-surface';
  const actionChips = ACTION_CHIPS_BY_TAB[tabId] ?? [];
  const actRows = actListQuery.data?.activity_logs ?? [];
  const smsRows = smsListQuery.data?.sms_logs ?? [];
  const st = smsStatsQuery.data;
  const stats = mode === 'logs'
    ? [
        { label: 'Total today', value: actStatQueries[0]?.data?.pagination?.total, color: 'text-content' },
        { label: 'Sales today', value: actStatQueries[1]?.data?.pagination?.total, color: 'text-blue-600 dark:text-blue-400' },
        { label: 'Field ops today', value: actStatQueries[2]?.data?.pagination?.total, color: 'text-rose-600 dark:text-rose-400' },
        { label: 'People today', value: actStatQueries[3]?.data?.pagination?.total, color: 'text-emerald-600 dark:text-emerald-400' },
      ]
    : [
        { label: 'Total', value: st?.total, color: 'text-content' },
        { label: 'Sent', value: st?.SENT, color: 'text-emerald-600 dark:text-emerald-400' },
        { label: 'Failed', value: st?.FAILED, color: 'text-rose-600 dark:text-rose-400' },
        { label: 'Skipped', value: st?.SKIPPED, color: 'text-amber-600 dark:text-amber-400' },
      ];

  const ToggleBtn = ({ m, icon, label }: { m: Mode; icon: React.ReactNode; label: string }) => (
    <button type="button" onClick={() => setMode(m)}
      className={cn('inline-flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-xs font-semibold transition', mode === m ? 'bg-surface text-content shadow-soft' : 'text-muted hover:text-content')}>
      {icon} {label}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Header + mode toggle */}
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="inline-flex items-center gap-2 text-xl font-semibold text-content">
            {mode === 'logs' ? <ScrollText className="size-6 text-brand-600" /> : <MessageCircle className="size-6 text-brand-600" />}
            {mode === 'logs' ? 'Activity Logs' : 'WhatsApp Sends'}
          </h1>
          <p className="mt-0.5 text-sm text-muted">
            {mode === 'logs'
              ? 'Audit trail of every staff action — sales, billing, inventory, field ops, people, inquiries and sign-in events.'
              : 'One row per WhatsApp send attempt — Sent / Failed / Skipped / Stub (dev). Delivered & read receipts are not tracked.'}
          </p>
        </div>
        <div className="inline-flex items-center gap-1 rounded-[10px] border border-line bg-surface-2 p-1">
          <ToggleBtn m="logs" icon={<ScrollText className="size-3.5" />} label="Activity" />
          <ToggleBtn m="whatsapp" icon={<MessageCircle className="size-3.5" />} label="WhatsApp" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-card border border-line bg-surface p-4 shadow-soft">
            <div className="text-2xs uppercase tracking-wide text-muted">{s.label}</div>
            <div className={cn('mt-1 text-2xl font-bold tabular-nums', s.color)}>{s.value ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-line bg-surface p-3 shadow-soft">
        {mode === 'logs' ? (
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search description…" className={cn(inputCls, 'w-full pl-9')} />
          </div>
        ) : (
          <>
            <input value={smsTemplate} onChange={(e) => setSmsTemplate(e.target.value)} placeholder="Template" className={cn(inputCls, 'min-w-[160px] flex-1')} />
            <input value={smsMobile} onChange={(e) => setSmsMobile(e.target.value)} placeholder="Mobile" className={cn(inputCls, 'w-36')} />
          </>
        )}
        <DatePicker.RangePicker
          value={[fromDate ? dayjs(fromDate) : null, toDate ? dayjs(toDate) : null]}
          onChange={(v) => { setFromDate(v?.[0] ? v[0].format('YYYY-MM-DD') : ''); setToDate(v?.[1] ? v[1].format('YYYY-MM-DD') : ''); }}
          disabledDate={disableFuture}
          format="DD-MM-YYYY"
          allowEmpty={[true, true]}
          placeholder={['From', 'To']}
          aria-label="Date range"
        />
        {mode === 'logs' && <ActorFilter value={actorId} onChange={setActorId} />}
        <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-[10px] border border-line px-3 py-2 text-sm text-content hover:bg-surface-2"><RotateCcw className="size-3.5" /> Reset</button>
      </div>

      {/* Tabs (activity) / status chips (whatsapp) + table */}
      <div className="rounded-card border border-line bg-surface shadow-soft">
        {mode === 'logs' ? (
          <>
            <div className="flex flex-wrap gap-1 overflow-x-auto border-b border-line px-2 py-2">
              {TABS.map((t) => {
                const active = tabId === t.id; const count = tabCounts[t.id];
                return (
                  <button key={t.id} onClick={() => setTabId(t.id)} className={cn('inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-sm font-medium transition', active ? 'border-brand-500 bg-brand-50 text-brand-800 dark:bg-brand-900/20 dark:text-brand-200' : 'border-transparent text-content hover:bg-surface-2')}>
                    {t.label}
                    {count != null && <span className={cn('ml-1 inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full px-1.5 text-2xs font-bold', active ? 'bg-brand-600 text-white' : 'bg-surface-2 text-muted')}>{count > 999 ? '999+' : count}</span>}
                  </button>
                );
              })}
            </div>
            {actionChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 border-b border-line bg-surface-2/40 px-3 py-2">
                <button onClick={() => setActionFilter('')} className={cn(chipBase, actionFilter === '' ? chipActive : chipIdle)}>Any action</button>
                {actionChips.map((a) => <button key={a} onClick={() => setActionFilter(actionFilter === a ? '' : a)} className={cn(chipBase, actionFilter === a ? chipActive : chipIdle)}>{a.replace(/_/g, ' ')}</button>)}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-wrap gap-1.5 border-b border-line bg-surface-2/40 px-3 py-2">
            {SMS_STATUSES.map((s) => (
              <button key={s || 'all'} onClick={() => setSmsStatus(s)} className={cn(chipBase, smsStatus === s ? chipActive : chipIdle)}>{s || 'All'}</button>
            ))}
          </div>
        )}

        <div className="overflow-x-auto p-2">
          {mode === 'logs' ? (
            <table className="w-full min-w-[760px] text-sm">
              <thead><tr className="text-left text-2xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2.5 font-semibold">When</th><th className="px-3 py-2.5 font-semibold">Actor</th><th className="px-3 py-2.5 font-semibold">Module</th><th className="px-3 py-2.5 font-semibold">Action</th><th className="px-3 py-2.5 font-semibold">Description</th><th className="px-3 py-2.5 font-semibold">Record</th>
              </tr></thead>
              <tbody>
                {isLoading ? <tr><td colSpan={6} className="px-3 py-10 text-center text-muted">Loading…</td></tr>
                : actRows.length === 0 ? <tr><td colSpan={6} className="px-3 py-10 text-center text-muted">No activity matches these filters.</td></tr>
                : actRows.map((r) => (
                  <tr key={r.id} onClick={() => setSelected(r)} className="cursor-pointer border-t border-line hover:bg-surface-2/60">
                    <td className="px-3 py-2.5"><div className="text-xs font-medium text-content">{formatIst(r.createdAt)}</div><div className="text-2xs text-muted">{relTime(r.createdAt)}</div></td>
                    <td className="px-3 py-2.5">{r.actor ? <div className="leading-tight"><div className="truncate text-sm font-medium text-content">{r.actor.name || r.actor.phone}</div><div className="text-2xs uppercase tracking-widest text-muted">{r.actor.role}</div></div> : <span className="text-xs italic text-muted">{r.source || 'System'}</span>}</td>
                    <td className="px-3 py-2.5"><span className={cn('inline-block rounded px-2 py-0.5 text-2xs font-medium', MODULE_BADGE[r.module] ?? 'bg-surface-2 text-content')}>{r.module}</span></td>
                    <td className="px-3 py-2.5"><span className={cn('inline-block rounded px-2 py-0.5 text-2xs font-medium', actionBadge(r.action))}>{r.action.replace(/_/g, ' ')}</span></td>
                    <td className="max-w-[360px] px-3 py-2.5"><div className="truncate text-content" title={r.message ?? ''}>{r.message || <span className="italic text-muted">—</span>}</div></td>
                    <td className="px-3 py-2.5">{r.referenceLabel ? <span className="font-mono text-xs text-content">{r.referenceLabel}</span> : <span className="text-xs italic text-muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[760px] text-sm">
              <thead><tr className="text-left text-2xs uppercase tracking-wide text-muted">
                <th className="px-3 py-2.5 font-semibold">When</th><th className="px-3 py-2.5 font-semibold">Status</th><th className="px-3 py-2.5 font-semibold">To</th><th className="px-3 py-2.5 font-semibold">Template</th><th className="px-3 py-2.5 font-semibold">By</th><th className="px-3 py-2.5 font-semibold">Message</th>
              </tr></thead>
              <tbody>
                {isLoading ? <tr><td colSpan={6} className="px-3 py-10 text-center text-muted">Loading…</td></tr>
                : smsRows.length === 0 ? <tr><td colSpan={6} className="px-3 py-10 text-center text-muted">No WhatsApp sends match these filters.</td></tr>
                : smsRows.map((r) => (
                  <tr key={r.id} onClick={() => setSmsSelected(r)} className="cursor-pointer border-t border-line hover:bg-surface-2/60">
                    <td className="px-3 py-2.5"><div className="text-xs font-medium text-content">{formatIst(r.createdAt)}</div><div className="text-2xs text-muted">{relTime(r.createdAt)}</div></td>
                    <td className="px-3 py-2.5"><span className={cn('inline-block rounded px-2 py-0.5 text-2xs font-medium', SMS_STATUS_BADGE[r.status] ?? 'bg-surface-2 text-content')}>{r.status || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="font-mono text-xs text-content">{r.mobile || '—'}</span></td>
                    <td className="px-3 py-2.5"><div className="leading-tight"><div className="truncate text-xs font-medium text-content" title={r.templateName}>{r.templateName || '—'}</div><div className="text-2xs uppercase tracking-widest text-muted">{r.templateType || 'utility'}</div></div></td>
                    <td className="px-3 py-2.5"><span className="truncate text-xs text-content">{r.adminName || 'system'}</span></td>
                    <td className="max-w-[320px] px-3 py-2.5"><div className="truncate text-content" title={r.messageText ?? ''}>{r.messageText || <span className="italic text-muted">—</span>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          <div className="mt-3 flex items-center justify-between text-xs text-muted">
            <div>{pg.total ? `Showing ${(pg.page - 1) * PAGE_SIZE + 1}–${Math.min(pg.page * PAGE_SIZE, pg.total)} of ${pg.total}` : mode === 'logs' ? '0 events' : '0 sends'}</div>
            <div className="inline-flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pg.page <= 1 || isFetching} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50"><ChevronLeft className="size-3.5" /> Prev</button>
              <span>Page {pg.page} / {pg.pages || 1}</span>
              <button onClick={() => setPage((p) => p + 1)} disabled={pg.page >= (pg.pages || 1) || isFetching} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1 hover:bg-surface-2 disabled:opacity-50">Next <ChevronRight className="size-3.5" /></button>
            </div>
          </div>
        </div>
      </div>

      {selected && <ActivityLogDetailDrawer log={selected} onClose={() => setSelected(null)} />}
      {smsSelected && <SmsLogDetailDrawer log={smsSelected} onClose={() => setSmsSelected(null)} />}
    </div>
  );
}
